import { SvgIcon } from "@mui/material";
import { Bank, Settings01 } from "@untitled-ui/icons-react";
import { ClipboardCheck, CoinsHand, Cube01, File07, BankNote03 } from "@untitled-ui/icons-react/build/esm";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { paths } from "~/paths";
import { useGetCurrentCompanyCache } from "~/queries/useGetCurrentCompany";
import type { ReactNode } from "react";
import { usePermissionFilters } from "~/hooks/use-permission-filters";
import { AccountBalanceWalletOutlined, ImportantDevicesOutlined } from "@mui/icons-material";
export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}
export interface Section {
  items: Item[];
  subheader?: string;
}
export interface EnabledTabs {
  agent: boolean;
  client: boolean;
  product: boolean;
  report: boolean;
  finance: boolean;
  accountTransaction: boolean;
  procurement: boolean;
}
export const useSections = () => {
  const {
    data
  } = useGetCurrentCompanyCache();
  const {
    t
  } = useTranslation();
  const permissions = usePermissionFilters();
  const enabledTab = useMemo(() => {
    return {
      agent: permissions.VIEW_AGENT,
      client: permissions.VIEW_CLIENT,
      product: permissions.VIEW_PRODUCT,
      report: permissions.VIEW_REPORT,
      finance: permissions.VIEW_FINANCE,
      isUserTaskManagementEnabled: data?.isUserTaskManagementEnabled === true ? true : false,
      accountTransaction: data?.accountTransaction === true && (permissions.ACCESS_ACCOUNT || permissions.ACCESS_PAYMENT_ATTACHMENT || permissions.ACCESS_TRANSACTION) ? true : false,
      procurement: data?.isProcurementManagementEnabled === true ? true : false
    };
  }, [data, permissions]);
  const getAccountTransactionPath = () => {
    if (permissions.ACCESS_ACCOUNT) {
      return paths.accountTransactions;
    }
    if (permissions.ACCESS_PAYMENT_ATTACHMENT) {
      return paths.accountTransactions.replace("accountsTab=accounts", "accountsTab=payment-attachments");
    }
    if (permissions.ACCESS_TRANSACTION) {
      return paths.accountTransactions.replace("accountsTab=accounts", "accountsTab=transactions");
    }
    return paths.accountTransactions;
  };
  return useMemo<Section[]>(() => {
    return [enabledTab.isUserTaskManagementEnabled && {
      items: [{
        title: "Task Management",
        path: paths.userTaskManagement,
        icon: <SvgIcon fontSize="small">
								<CoinsHand />
							</SvgIcon>
      }]
    }, {
      items: [{
        title: data?.fileAlias?.plural ?? "",
        path: paths.index,
        icon: <SvgIcon fontSize="small">
								<ClipboardCheck />
							</SvgIcon>
      }]
    }, enabledTab.agent && {
      items: [{
        title: "Agents",
        path: paths.agents.list,
        icon: <SvgIcon fontSize="small">
								<CoinsHand />
							</SvgIcon>
      }]
    }, enabledTab.client && {
      items: [{
        title: "Clients",
        path: paths.clients.list,
        icon: <SvgIcon fontSize="small">
								<Bank />
							</SvgIcon>
      }]
    }, enabledTab.product && {
      items: [{
        title: "Products",
        path: paths.products.list,
        icon: <SvgIcon fontSize="small">
								<Cube01 />
							</SvgIcon>
      }]
    }, {
      items: [{
        title: "Fix Discount",
        path: paths.fixDiscounts,
        icon: <SvgIcon fontSize="small">
								<BankNote03 />
							</SvgIcon>
      }]
    }, {
      items: [{
        title: "Additional Fee",
        path: paths.additionalFees,
        icon: <SvgIcon fontSize="small">
								<BankNote03 />
							</SvgIcon>
      }]
    }, {
      items: [{
        title: data?.fileAlias?.singular ? data?.fileAlias?.singular + " History" : "Files History",
        path: paths.fileHistory,
        icon: <SvgIcon fontSize="small">
								<ClipboardCheck />
							</SvgIcon>
      }]
    }, enabledTab.report && {
      items: [{
        title: "Reports",
        path: paths.reports,
        icon: <SvgIcon fontSize="small">
								<File07 />
							</SvgIcon>
      }]
    }, enabledTab.finance && {
      items: [{
        title: "Finances",
        path: paths.fiancances,
        icon: <SvgIcon fontSize="small">
								<File07 />
							</SvgIcon>
      }]
    }, enabledTab.accountTransaction && {
      items: [{
        title: "Account Transactions",
        path: getAccountTransactionPath(),
        icon: <SvgIcon fontSize="small">
								<AccountBalanceWalletOutlined />
							</SvgIcon>
      }]
    }, enabledTab.procurement && {
      items: [{
        title: "Procurement Management",
        path: paths.procurementManagement.suppliers.list,
        icon: <SvgIcon fontSize="small">
								<ImportantDevicesOutlined />
							</SvgIcon>
      }]
    }, {
      items: [{
        title: "Settings",
        path: paths.settings,
        icon: <SvgIcon fontSize="small">
								<Settings01 />
							</SvgIcon>
      }]
    }].filter(Boolean) as Section[];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, enabledTab]);
};